import Api from '@src/controllers/Api'
import queryString from 'query-string'
import { isAccessTokenValid, isRefreshTokenValid, markSessionExpiredLogout } from '@utils/commonUtil'

const AUTH_FLOW_URLS = [
  '/my/user/reminder/password',
  '/my/user/email',
  '/user/signup'
]

class LearningApi extends Api {
  constructor () {
    super()
    this.pendingRefreshToken = false
    this.afterRefreshTokenQueues = []
    this.isHandlingAuthFailure = false
  }

  setContext ( contexts ) {
    Object.assign(this,contexts)
  }

  _defineConfig () {
    return {
      baseURL: `${LEARNING_API_ENDPOINT}`,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      paramsSerializer: params => {
        return queryString.stringify(params,{arrayFormat: 'separator', arrayFormatSeparator: '|'});
      }
    }
  }

  _defineNetworkError (error) {
    // オフラインでも使えるようにするので処理等はなし
    console.error(error)
    // this.router.push({ name: 'Error', query: { error_type: 'networkError' } })
  }

  _isAuthFlowUrl (url) {
    return AUTH_FLOW_URLS.includes(url)
  }

  _forceLogout (error) {
    if (this.isHandlingAuthFailure) {
      return Promise.reject(error || new Error('Session expired'))
    }
    this.isHandlingAuthFailure = true
    this.afterRefreshTokenQueues.forEach((e) => e.reject())
    this.afterRefreshTokenQueues = []
    this.pendingRefreshToken = false
    this.resetAuth()
    const currentRoute = this.router?.currentRoute?.value
    if (currentRoute?.name !== 'Login') {
      $logger.info('Session expired, redirecting to login')
      markSessionExpiredLogout()
      return this.router.replace({ name: 'Login' }).finally(() => {
        this.isHandlingAuthFailure = false
      }).then(() => Promise.reject(error || new Error('Session expired')))
    }
    this.isHandlingAuthFailure = false
    return Promise.reject(error || new Error('Session expired'))
  }

  _executeRefreshToken () {
    this.pendingRefreshToken = true
    $logger.try('Refresh token')
    this.axiosInstance.get('/token', { custom: { isRefreshRequest: true } })
      .then((res) => {
        $logger.success('Refresh token')
        this.updateAccessToken(res.data.access_token)
        this.afterRefreshTokenQueues.forEach((e) => e.resolve())
        this.afterRefreshTokenQueues = []
      })
      .catch((err) => {
        $logger.failure('Refresh token', err.message)
        this._forceLogout(err)
      })
      .finally(() => {
        this.pendingRefreshToken = false
      })
  }

  _enqueueRefreshWait (rejectError) {
    return new Promise((resolve, reject) => {
      if (!this.pendingRefreshToken) {
        this._executeRefreshToken()
      }
      this.afterRefreshTokenQueues.push({
        resolve: () => resolve(),
        reject: () => reject(rejectError || new Error('Refresh failed'))
      })
    })
  }

  ensureFreshAccessToken () {
    return this._ensureFreshAccessToken()
  }

  forceLogout (error) {
    return this._forceLogout(error)
  }

  _ensureFreshAccessToken () {
    const token = this.accessToken.value
    if (!token) return Promise.resolve()
    if (isAccessTokenValid(token)) return Promise.resolve()
    if (!isRefreshTokenValid(token)) {
      return this._forceLogout()
    }
    return this._enqueueRefreshWait()
  }

  // _defineResponseError 内で _retryAfterRefreshToken 利用すること
  _retryAfterRefreshToken (error) {
    // 公開APIは認証エラー時もログイン画面へ遷移しない
    if (error.config?.custom?.isPublic) {
      return Promise.reject(error)
    }

    // 401以外のエラーはトークン切れではないのでそのままリジェクトを返す
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error)

    // 401認証エラー
    } else {
      // メール変更のpincodeが間違っている場合もここに来るので、configの値で分岐
      try {
        if (error.config?.url && this._isAuthFlowUrl(error.config.url)) {
          return Promise.reject(error)
        }
      } catch (e) {
        console.error('_retryAfterRefreshToken error', e)
      }

      // リフレッシュAPI自体の失敗はキューに入れず即ログアウト
      if (error.config?.url === '/token' || error.config?.custom?.isRefreshRequest) {
        return this._forceLogout(error)
      }

      if (!this.userLogging.value) {
        // ログインしていない場合はそのままログイン画面に遷移
        $logger.info('Unauthorized(Not logged in)')
        this.router.replace({ name: 'Login' })
        return Promise.reject(error)
      }

      // リフレッシュ後の再試行でも401の場合はログアウト
      if (error.config.custom?.isAfterRefreshToken) {
        return this._forceLogout(error)
      }

      // exp_refresh切れの場合はリフレッシュしない
      if (!isRefreshTokenValid(this.accessToken.value)) {
        return this._forceLogout(error)
      }
    }

    if (typeof error.config.custom === 'undefined') {
      error.config.custom = {}
    }

    error.config.custom.isAfterRefreshToken = true

    return this._enqueueRefreshWait(error).then(() => {
      return this.axiosInstance.request(error.config)
    })
  }

  _handleError500 (error) {
    const status = error.response.status
    if (status === 503) {
      this.router.push({ name: 'Maintenance' })
    } else {
      return false
    }
  }

  _defineResponseError (error) {
    this._handleError500(error)
    return this._retryAfterRefreshToken(error)
  }

  _defineRequestBefore (config) {
    // メール変更・パスワードリマインダー用
    if (config?.data?.headers?.tempAuthorization) {
      config.headers.Authorization = `Bearer ${config.data.headers.tempAuthorization}`
      delete config.data.headers
      return config
    }

    if (config.url === '/token' || config?.custom?.isRefreshRequest) {
      if (this.accessToken.value !== null) {
        config.headers.Authorization = `Bearer ${this.accessToken.value}`
      }
      return config
    }

    if (this.accessToken.value === null) {
      return config
    }

    if (isAccessTokenValid(this.accessToken.value)) {
      config.headers.Authorization = `Bearer ${this.accessToken.value}`
      return config
    }

    if (!isRefreshTokenValid(this.accessToken.value)) {
      return this._forceLogout()
    }

    return this._ensureFreshAccessToken().then(() => {
      config.headers.Authorization = `Bearer ${this.accessToken.value}`
      return config
    })
  }

  _defineResponseBefore (response) {
    return response
  }
}

export default new LearningApi().init()
